import ExpandMoreSharpIcon from '@mui/icons-material/ExpandMoreSharp';
import PlaylistAddCheckSharpIcon from '@mui/icons-material/PlaylistAddCheckSharp';
import { Box, Divider, Grid, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider, useFieldArray, FieldValues } from 'react-hook-form';
import { IFilters } from 'wisestep-api';
import {
  SearchBarNew,
  WsAutoComplete,
  WsBadge,
  WsBadgeSize,
  WsBadgetype,
  WsButton,
  WsColors,
  WsColorsNew,
  WsDatePicker,
  WsIconText,
  WsSelect,
  WsTypoColors,
  WsTypoVariants,
  formattedDate,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import { ContactAutocomplete } from '../../../components/ContactAutocomplete';
import RecruiterSuggestions from '../../../components/filters/components/RecruiterSuggestions';
import { ListingSearchPropsForNotesListing } from '../../../features/clientDetails/components/notes/helper/notesHelper';
import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import SmartFilter from '../../../features/submissionsNew/components/filters/smart/SmartFilter';
import {
  getBadges,
  getFilterValue,
  hasFilterValue,
  notesDefaultFilterValues,
  statusArray,
} from '../helper/NotesHelper';

interface INotesFilter {
  onSubmit: (filterValues) => void;
  defaultValues?: any;
  defaultSearchFiltersValues: ListingSearchPropsForNotesListing;
  tags: string[];
  toggleParent: string;
  handleArchiveNote: (value) => void;
  filterValue?: IFilters;
}

export const StyleBox = styled(Box)(() => ({
  border: '1px solid #E0E0E0',
  borderRadius: 10,
  paddingTop: 5,
  alignItems: 'center',
  backgroundColor: WsColors.TertiaryShades50,
  paddingLeft: 9,
}));

const StyledGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  backgroundColor: WsColors.White,
  boxShadow: `${theme.spacing(0)} ${theme.spacing(0.125)} ${theme.spacing(
    0.375
  )} rgba(0, 0, 0, 0.12)`,
  borderRadius: 2,
  height: 'auto',
}));

const NotesFilters = ({
  onSubmit,
  defaultSearchFiltersValues,
  tags,
  toggleParent,
  handleArchiveNote,
  filterValue,
}: INotesFilter) => {
  const [selectedDate, setSelectedDate] = useState<string>();
  const [tagsFilterValue, setTagsFilterValue] = useState<string[]>([]);
  const methods = useForm({
    defaultValues: { ...defaultSearchFiltersValues },
  });
  const { control, handleSubmit, reset, getValues, watch, setValue } = methods;

  const fromDate = watch('fromDate');
  const toDate = watch('toDate');

  const handleReset = () => {
    reset(notesDefaultFilterValues);
    setSelectedDate(undefined);
    onSubmit(notesDefaultFilterValues);
    setTagsFilterValue([]);
  };

  useEffect(() => {
    fromDate &&
      toDate &&
      setSelectedDate(`${formattedDate(`${fromDate}`)} to ${formattedDate(`${toDate}`)}`);
  }, [fromDate, toDate]);

  const onTagsSelection = async (tagsInput: string[]) => {
    setTagsFilterValue(tagsInput);
    methods.setValue('tags', tagsInput);
  };

  const handleApply = () => handleSubmit(onSubmit)();

  const onCreatedOrUpdatedBetweenClear = () => {
    reset({
      ...getValues(),
      fromDate: null,
      toDate: null,
    });
    setSelectedDate(undefined);
    onSubmit(getValues());
  };

  const onFilterClear = (field: 'createdBy' | 'tags', value: string) => {
    const values = getValues();
    const fieldValues = values[field];
    const updatedValues: string[] | undefined =
      fieldValues && Array.isArray(fieldValues)
        ? fieldValues.filter((item) => item !== value)
        : undefined;
    setValue(field, updatedValues);
    handleSubmit(onSubmit)();
  };

  const onCreatedByIdClear = (value: string) => onFilterClear('createdBy', value);

  const onClearTag = (value: string) => onFilterClear('tags', value);

  const onClentClear = (value: number) => {
    removeClient(value);
    handleSubmit(onSubmit)();
  };
  const onContactClear = (value: number) => {
    removeContact(value);
    handleSubmit(onSubmit)();
  };

  const { remove: removeContact } = useFieldArray({
    control,
    name: 'contacts',
  });

  const { remove: removeClient } = useFieldArray({
    control,
    name: 'clients',
  });
  const onClearReminder = () => {
    reset({
      ...getValues(),
      reminderStatus: '',
    });
    onSubmit(getValues());
  };

  const onFormSubmit = (data: FieldValues) => {
    const formData: ListingSearchPropsForNotesListing = {
      fromDate: data.fromDate,
      toDate: data.toDate,
      createdBy: data.createdBy,
      clientIds: data.clientIds,
      contactIds: data.contactIds,
      type: data.type,
      searchAcross: data.searchAcross,
      noteType: data.noteType,
      clientNames: data.clientNames,
      clients: data.clients,
      contacts: data.contacts,
      tags: data.tags,
      reminderStatus: data.reminderStatus,
      search: data.query ?? '',
    };
    onSubmit(formData);
  };
  const handleSearch = () => {
    const query = watch('query');
    onSubmit({
      ...getValues(),
      search: query,
    });
  };

  const onClearFilter = (filterType: string, label?: string, index?: number) => {
    const handlers: Record<string, () => void> = {
      dateRange: () => onCreatedOrUpdatedBetweenClear?.(),
      createdBy: () => onCreatedByIdClear?.(label ?? ''),
      client: () => onClentClear?.(index ?? -1),
      contact: () => onContactClear?.(index ?? -1),
      tag: () => onClearTag?.(label ?? ''),
      reminder: () => onClearReminder?.(),
    };
    handlers[filterType]?.();
  };

  return (
    <FormProvider {...methods}>
      <StyledGrid container mb={2} onSubmit={handleSubmit(onFormSubmit)}>
        <Grid item xs={4} p={2}>
          <SearchBarNew
            size="small"
            name="query"
            placeholder="Search"
            type="submit"
            onClear={handleReset}
            onSearch={() => handleSearch()}
          />
        </Grid>

        <Grid container spacing={0} mt={2} justifyContent="normal" xs={7}>
          <Grid item>
            <SmartFilter
              filterName="Date"
              onClick={handleSubmit(onFormSubmit)}
              filterValue={selectedDate ?? 'All'}
            >
              <WsDatePicker name="fromDate" label="From Date" disableFuture={true} />
              <WsDatePicker
                name="toDate"
                label="To Date"
                disableFuture={true}
                minDate={undefined}
              />
            </SmartFilter>
          </Grid>
          <Grid item>
            <SmartFilter
              filterName="Created By"
              onClick={handleApply}
              filterValue={getFilterValue('createdBy', methods, defaultSearchFiltersValues)}
            >
              <RecruiterSuggestions
                name="createdBy"
                multiple={true}
                defaultValue={methods.getValues('createdBy')}
              />
            </SmartFilter>
          </Grid>
          <Grid item>
            <SmartFilter
              filterName="Clients"
              onClick={handleApply}
              filterValue={getFilterValue('clients', methods, defaultSearchFiltersValues)}
            >
              <ClientAutocomplete name="clients" multiple />
            </SmartFilter>
          </Grid>
          <Grid item>
            <SmartFilter
              filterName="Contacts"
              onClick={handleApply}
              filterValue={getFilterValue('contacts', methods, defaultSearchFiltersValues)}
            >
              <ContactAutocomplete name="contacts" multiple />
            </SmartFilter>
          </Grid>
          <Grid item>
            <SmartFilter
              filterName="Tags"
              onClick={handleApply}
              filterValue={getFilterValue('tags', methods)}
            >
              <WsAutoComplete
                onTagsChange={onTagsSelection}
                placeholder="Eg, Michael"
                initialValue={tagsFilterValue}
                value={tagsFilterValue}
                optionsInStringArray={tags}
                multiple={true}
                freeSolo={true}
                name="tags"
                size="small"
              />
            </SmartFilter>
          </Grid>
          <Grid item>
            <SmartFilter
              filterName="Reminders"
              onClick={handleApply}
              filterValue={getFilterValue('reminderStatus', methods)}
            >
              <WsSelect
                placeholder="Eg, Michael"
                name="reminderStatus"
                label=""
                selectArray={statusArray}
              />
            </SmartFilter>
          </Grid>
        </Grid>
        <Grid item xs={1} justifyContent="space-between" alignItems="center" sx={{ pl: 2, pt: 1 }}>
          <PopoverOnClick
            element={
              <StyleBox>
                <PlaylistAddCheckSharpIcon sx={{ fontSize: 25, color: WsColors.TheBlues500 }} />
                <ExpandMoreSharpIcon sx={{ fontSize: 25, color: WsColors.TheBlues500 }} />
              </StyleBox>
            }
          >
            <Box p={2}>
              <WsIconText
                icon={<></>}
                text="Active Notes"
                textCursor="pointer"
                textColor={toggleParent == 'ACTIVE' ? WsTypoColors.PRIMARY600 : WsTypoColors.BLACK}
                textSize={WsTypoVariants.SUB_TITLE}
                onClick={() => handleArchiveNote('ACTIVE')}
              />
            </Box>
            <Box p={2}>
              <WsIconText
                icon={<></>}
                text="Archived Notes"
                textCursor="pointer"
                textColor={toggleParent != 'ACTIVE' ? WsTypoColors.PRIMARY600 : WsTypoColors.BLACK}
                textSize={WsTypoVariants.SUB_TITLE}
                onClick={() => handleArchiveNote('ARCHIVE')}
              />
            </Box>
          </PopoverOnClick>
        </Grid>

        <Grid container p={2} spacing={1} alignItems="center">
          {getBadges({
            getValues,
            onClearFilter,
            filterValue,
          }).map((b) => (
            <Grid item key={b.key}>
              <WsBadge
                label={b.label}
                badgeType={WsBadgetype.INACTIVE}
                endIcon={true}
                onClick={b.onClick}
                menuList={false}
                badgeSize={WsBadgeSize.LARGE}
              />
            </Grid>
          ))}

          {hasFilterValue(filterValue) && (
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  backgroundColor: WsColorsNew.neutral_400a,
                  height: 30,
                  mx: 0.5,
                }}
              />
              <WsButton onClick={handleReset} variant="text">
                Clear Filters
              </WsButton>
            </Grid>
          )}
        </Grid>
      </StyledGrid>
    </FormProvider>
  );
};

export default NotesFilters;
